/**
 * 区块树的遍历与改写。
 *
 * 分栏是「区块里再嵌区块」：ColumnsSection.columns 里每一栏都是一个区块数组。
 * 在它出现之前所有代码都假设 template.sections 是一层平的；现在凡是按 id 找
 * 区块、改区块、删区块的地方都得走这里，否则栏里的区块就是「画布上能点到、
 * 属性栏改了不生效」。
 */
import type { ColumnsSection, ContractSection } from './types';

export function isColumns(s: ContractSection): s is ColumnsSection {
  return s.type === 'columns';
}

/** 深度优先遍历所有区块（含分栏本身和栏里的）。 */
export function* walkSections(sections: ContractSection[]): Generator<ContractSection> {
  for (const s of sections) {
    yield s;
    if (isColumns(s)) for (const col of s.columns) yield* walkSections(col);
  }
}

export function flattenSections(sections: ContractSection[]): ContractSection[] {
  return [...walkSections(sections)];
}

export function findSectionDeep(sections: ContractSection[], id: string): ContractSection | null {
  for (const s of walkSections(sections)) if (s.id === id) return s;
  return null;
}

/** 对每个区块（含嵌套）套用 fn；fn 返回同一个对象时不产生新数组。 */
export function mapSectionsDeep(
  sections: ContractSection[],
  fn: (s: ContractSection) => ContractSection,
): ContractSection[] {
  let changed = false;
  const out = sections.map((s) => {
    let cur: ContractSection = s;
    if (isColumns(s)) {
      const cols = s.columns.map((col) => mapSectionsDeep(col, fn));
      if (cols.some((c, i) => c !== s.columns[i])) cur = { ...s, columns: cols } as ColumnsSection;
    }
    const next = fn(cur);
    if (next !== s) changed = true;
    return next;
  });
  return changed ? out : sections;
}

export function updateSectionDeep(
  sections: ContractSection[],
  id: string,
  updated: ContractSection,
): ContractSection[] {
  return mapSectionsDeep(sections, (s) => (s.id === id ? updated : s));
}

export function removeSectionDeep(sections: ContractSection[], id: string): ContractSection[] {
  const kept = sections.filter((s) => s.id !== id);
  return kept.map((s) =>
    isColumns(s) ? ({ ...s, columns: s.columns.map((col) => removeSectionDeep(col, id)) } as ContractSection) : s,
  );
}

/** id 所在的那个数组（顶层或某一栏）和它在里面的下标。 */
export function locateSection(
  sections: ContractSection[],
  id: string,
): { list: ContractSection[]; index: number; parent: ColumnsSection | null; col: number } | null {
  const idx = sections.findIndex((s) => s.id === id);
  if (idx !== -1) return { list: sections, index: idx, parent: null, col: -1 };
  for (const s of sections) {
    if (!isColumns(s)) continue;
    for (let c = 0; c < s.columns.length; c++) {
      const hit = locateSection(s.columns[c], id);
      if (hit) return hit.parent ? hit : { ...hit, parent: s, col: c };
    }
  }
  return null;
}

/** 把某个数组（顶层或某一栏）整个换掉。list 用引用比对。 */
function replaceList(
  sections: ContractSection[],
  target: ContractSection[],
  next: ContractSection[],
): ContractSection[] {
  if (sections === target) return next;
  return sections.map((s) =>
    isColumns(s)
      ? ({ ...s, columns: s.columns.map((col) => replaceList(col, target, next)) } as ContractSection)
      : s,
  );
}

/** 在 afterId 所在的那一层、它后面插入；找不到就追加到顶层末尾。 */
export function insertAfterDeep(
  sections: ContractSection[],
  afterId: string,
  block: ContractSection,
): ContractSection[] {
  const hit = locateSection(sections, afterId);
  if (!hit) return [...sections, block];
  const next = [...hit.list];
  next.splice(hit.index + 1, 0, block);
  return replaceList(sections, hit.list, next);
}

/** 在某一栏（或顶层，parentId 为空）的 index 位置插入。 */
export function insertAtDeep(
  sections: ContractSection[],
  block: ContractSection,
  index: number,
  parentId?: string | null,
  col?: number,
): ContractSection[] {
  if (!parentId) {
    const next = [...sections];
    next.splice(Math.max(0, Math.min(index, next.length)), 0, block);
    return next;
  }
  return mapSectionsDeep(sections, (s) => {
    if (s.id !== parentId || !isColumns(s)) return s;
    const c = Math.max(0, Math.min(col ?? 0, s.columns.length - 1));
    const list = [...(s.columns[c] ?? [])];
    list.splice(Math.max(0, Math.min(index, list.length)), 0, block);
    const columns = s.columns.map((x, i) => (i === c ? list : x));
    return { ...s, columns } as ContractSection;
  });
}

/** 同一层内上下移动。 */
export function moveSectionDeep(sections: ContractSection[], id: string, dir: -1 | 1): ContractSection[] {
  const hit = locateSection(sections, id);
  if (!hit) return sections;
  const to = hit.index + dir;
  if (to < 0 || to >= hit.list.length) return sections;
  const next = [...hit.list];
  [next[hit.index], next[to]] = [next[to], next[hit.index]];
  return replaceList(sections, hit.list, next);
}

/** 拖到目标区块的前/后，跨层也行（从原处摘掉，塞进目标所在的那一层）。 */
export function reorderDeep(
  sections: ContractSection[],
  id: string,
  targetId: string,
  before: boolean,
): ContractSection[] {
  if (id === targetId) return sections;
  const moved = findSectionDeep(sections, id);
  if (!moved) return sections;
  // 不能把分栏塞进自己的栏里
  if (isColumns(moved) && findSectionDeep(moved.columns.flat(), targetId)) return sections;
  const without = removeSectionDeep(sections, id);
  const hit = locateSection(without, targetId);
  if (!hit) return sections;
  const next = [...hit.list];
  next.splice(before ? hit.index : hit.index + 1, 0, moved);
  return replaceList(without, hit.list, next);
}
